import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { BiLock } from "react-icons/bi";
import { useStateValue } from "../../context/StateProvider";
import { fetchPayment } from "../../api/payment";
import Loader from "../Loader";

const Body = () => {
    const [{ userCartInfo, cartItems, cartTotal, payment }, dispatch] = useStateValue();
    const formRef = useRef<HTMLFormElement>(null);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [showRetryButton, setShowRetryButton] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isMobile, setIsMobile] = useState(false);

    // Проверка на мобильное устройство
    useEffect(() => {
        const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
        if (/android|iPad|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
            setIsMobile(true);
        }
    }, []);

    // Эффект для автоматического перенаправления при получении данных
    useEffect(() => {
        if (payment && typeof payment === 'object' && Object.keys(payment).length > 0) {
            const requiredFields = [
                'uniteller_shop_idp',
                'uniteller_order_idp',
                'uniteller_subtotal_p',
                'uniteller_signature'
            ];

            const missingFields = requiredFields.filter(field => !payment[field]);

            if (payment['uniteller_order_idp'] && payment['uniteller_order_idp'] !== "") {
                if (isRedirecting) return;

                const hasRedirected = sessionStorage.getItem('payment_redirected');

                if (!hasRedirected) {
                    redirectToPayment();
                } else {
                    setShowRetryButton(true);
                }
            }
        }
    }, [payment]);

    // Эффект для запроса данных при загрузке
    useEffect(() => {
        if (payment && typeof payment === 'object' && Object.keys(payment).length > 0) {
            if (payment['uniteller_order_idp'] && payment['uniteller_order_idp'] !== "") {
                return;
            }
        }

        const data = {
            cartItems: cartItems,
            userCartInfo: userCartInfo,
            cartTotal: cartTotal
        };
        fetchPayment(data, dispatch);
    }, []);

    // Функция для создания URL с параметрами
    const createPaymentUrl = (form: HTMLFormElement): string => {
        const action = form.action;
        const inputs = form.querySelectorAll('input[type="hidden"]');
        const params: string[] = [];

        // Используем forEach с правильной типизацией
        inputs.forEach((input) => {
            const htmlInput = input as HTMLInputElement;
            if (htmlInput.name && htmlInput.value) {
                params.push(`${encodeURIComponent(htmlInput.name)}=${encodeURIComponent(htmlInput.value)}`);
            }
        });

        return `${action}?${params.join('&')}`;
    };

    // Перенаправление на оплату
    const redirectToPayment = () => {
        if (!payment || typeof payment !== 'object' || Object.keys(payment).length === 0) {
            setError('Данные для оплаты не загружены');
            setShowRetryButton(true);
            return;
        }

        const requiredFields = [
            'uniteller_shop_idp',
            'uniteller_order_idp',
            'uniteller_subtotal_p',
            'uniteller_signature'
        ];

        const missingFields = requiredFields.filter(field => !payment[field]);
        if (missingFields.length > 0) {
            setError('Ошибка в данных оплаты');
            setShowRetryButton(true);
            return;
        }

        if (!formRef.current) {
            setError('Ошибка системы');
            setShowRetryButton(true);
            return;
        }

        setIsRedirecting(true);
        sessionStorage.setItem('payment_redirected', 'true');
        sessionStorage.setItem('payment_order_id', payment['uniteller_order_idp'] || '');

        try {
            const form = formRef.current;

            if (isMobile) {
                // Для мобильных - используем URL с параметрами
                const url = createPaymentUrl(form);
                window.location.href = url;
            } else {
                // Для десктопа - отправляем форму
                form.target = '_blank';
                form.submit();
            }

            // Таймаут на случай, если форма не перенаправила
            setTimeout(() => {
                setIsRedirecting(false);
                setShowRetryButton(true);
            }, 10000);
        } catch (err) {
            console.error('Ошибка при отправке:', err);
            setIsRedirecting(false);
            setShowRetryButton(true);
            setError('Произошла ошибка. Попробуйте нажать кнопку оплаты.');
        }
    };

    // Ручная отправка формы (по кнопке)
    const handleManualSubmit = (e: React.MouseEvent) => {
        e.preventDefault();
        setError(null);

        if (!formRef.current) {
            setError('Ошибка системы');
            return;
        }

        setIsRedirecting(true);
        setShowRetryButton(false);

        try {
            const form = formRef.current;

            if (isMobile) {
                // Для мобильных - используем URL с параметрами
                const url = createPaymentUrl(form);
                window.location.href = url;
            } else {
                // Для десктопа - отправляем форму
                form.target = '_blank';
                form.submit();
            }

            setTimeout(() => {
                setIsRedirecting(false);
                setShowRetryButton(true);
            }, 8000);
        } catch (err) {
            console.error('Ошибка:', err);
            setIsRedirecting(false);
            setShowRetryButton(true);
            setError('Ошибка при отправке');
        }
    };

    // Если данные еще загружаются
    if (!payment || typeof payment !== 'object' || Object.keys(payment).length === 0) {
        return (
            <div className="w-full h-full bg-cartBg flex flex-col">
                <div className="w-full flex items-center justify-center mt-20">
                    <Loader progress="Подготовка к оплате..." />
                </div>
            </div>
        );
    }

    // Показываем состояние перенаправления
    if (isRedirecting) {
        return (
            <div className="w-full h-full bg-cartBg flex flex-col">
                <div className="w-full flex items-center justify-center mt-20">
                    <div className="text-center">
                        <div className="flex flex-col items-center gap-4">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-buttonColor"></div>
                            <p className="text-gray-600 text-lg">Перенаправление на страницу оплаты...</p>
                            <p className="text-sm text-gray-400">Пожалуйста, подождите</p>
                            <button
                                onClick={() => {
                                    setIsRedirecting(false);
                                    setShowRetryButton(true);
                                    setError('Перенаправление отменено');
                                }}
                                className="mt-4 text-sm text-gray-400 hover:text-gray-600 underline"
                            >
                                Отменить
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Показываем кнопку оплаты
    return (
        <div className="w-full h-full bg-cartBg flex flex-col">
            <div className="w-full flex items-center justify-center mt-20">
                <div className="w-[90%] max-w-md">
                    {/* Скрытая форма */}
                    <form
                        ref={formRef}
                        action="https://wpay.uniteller.ru/pay/"
                        method="POST"
                        style={{ display: 'none' }}
                    >
                        <input type="hidden" name="Shop_IDP" value={payment['uniteller_shop_idp'] || ''} />
                        <input type="hidden" name="Order_IDP" value={payment['uniteller_order_idp'] || ''} />
                        <input type="hidden" name="Customer_IDP" value={payment['uniteller_customer_idp'] || ''} />
                        <input type="hidden" name="Subtotal_P" value={payment['uniteller_subtotal_p'] || ''} />
                        <input type="hidden" name="Lifetime" value={payment['uniteller_lifetime'] || ''} />
                        <input type="hidden" name="Signature" value={payment['uniteller_signature'] || ''} />
                        <input type="hidden" name="URL_RETURN_OK" value={payment['uniteller_url_return_ok'] || ''} />
                        <input type="hidden" name="URL_RETURN_NO" value={payment['uniteller_url_return_fail'] || ''} />
                    </form>

                    {/* Сообщение об ошибке */}
                    {error && (
                        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-yellow-700 text-sm">{error}</p>
                        </div>
                    )}

                    {/* Кнопка оплаты */}
                    <motion.button
                        onClick={handleManualSubmit}
                        disabled={isRedirecting}
                        whileTap={{ scale: isRedirecting ? 1 : 0.95 }}
                        className={`
                            flex items-center justify-center gap-2 
                            w-full p-3 rounded-full 
                            bg-gradient-to-tr from-buttonColor to-buttonColor 
                            hover:from-buttonColor hover:to-buttonColor 
                            transition-all duration-75 ease-in-out 
                            text-gray-50 text-lg my-2 hover:shadow-lg
                            ${isRedirecting ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                    >
                        {isRedirecting ? (
                            <>
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Перенаправление...</span>
                            </>
                        ) : (
                            <>
                                <BiLock className="text-xl" />
                                <span>Оплатить {cartTotal ? `${cartTotal} ₽` : ''}</span>
                            </>
                        )}
                    </motion.button>

                    {/* Подсказка */}
                    {showRetryButton && !isRedirecting && (
                        <p className="text-center text-gray-400 text-sm mt-2">
                            Безопасная оплата через Uniteller
                        </p>
                    )}

                    {showRetryButton && !isRedirecting && !error && (
                        <p className="text-center text-gray-400 text-xs mt-1">
                            Нажмите кнопку, чтобы перейти к оплате
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Body;